import * as React from 'react'
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { useComputedDateCatalog } from '../../../api'

type RuleValue = string | string[]
type ValueRule = { value: RuleValue; note?: string }
type DerivedRule = { note: string }
type AllowedValuesRule = { values: string[]; note?: string }
type IgnoreFieldRule = { note?: string }

type CadencePolicy = {
  version: 1
  defaults: Record<string, ValueRule>
  enforced: Record<string, ValueRule>
  derived: Record<string, DerivedRule>
  requirements: {
    allowedValues: Record<string, AllowedValuesRule>
    ignoreFields: Record<string, IgnoreFieldRule>
  }
  notes?: string
}

type Props = {
  value: string
  onChange: (value: string, error: string | null) => void
}

type FieldKind = 'scalar' | 'array' | 'computedDateArray'

const FIELD_OPTIONS: { key: string; label: string; kind: FieldKind }[] = [
  { key: 'timezone', label: 'Timezone', kind: 'scalar' },
  { key: 'interval_update_secs', label: 'Interval Update (secs)', kind: 'scalar' },
  { key: 'interval_starting_at_time', label: 'Interval Starting At Time', kind: 'scalar' },
  { key: 'interval_ending_at_time', label: 'Interval Ending At Time', kind: 'scalar' },
  { key: 'interval_days_of_week', label: 'Interval Days Of Week', kind: 'scalar' },
  { key: 'arr_specific_times', label: 'Specific Times JSON', kind: 'scalar' },
  { key: 'interval_starting_at_dayofweek', label: 'Interval Starting Day Of Week', kind: 'scalar' },
  { key: 'interval_only_on_mmdd', label: 'Only On MM-DD', kind: 'array' },
  { key: 'interval_only_on_yyyymmdd', label: 'Only On YYYY-MM-DD', kind: 'array' },
  { key: 'interval_only_on_computed_dates', label: 'Only On Computed Dates', kind: 'computedDateArray' }
]

const FIELD_GUIDANCE: Record<string, { description: string; example: string }> = {
  timezone: {
    description: 'IANA timezone used when evaluating cadence timing.',
    example: 'America/New_York'
  },
  interval_update_secs: {
    description: 'Cadence discriminator in seconds (as string).',
    example: '86400'
  },
  interval_starting_at_time: {
    description: 'Start time for interval windows, or yearly start datetime.',
    example: '07:30 or 2026-06-11T10:00'
  },
  interval_ending_at_time: {
    description: 'End time for interval windows (24h HH:MM).',
    example: '00:00'
  },
  interval_days_of_week: {
    description: 'Comma-separated day names allowed for cadence.',
    example: 'Monday,Tuesday,Wednesday'
  },
  arr_specific_times: {
    description: 'Serialized JSON array string for daily specific times.',
    example: '[{"time":{"hour":"08","minutes":"00","amPm":"AM"}}]'
  },
  interval_starting_at_dayofweek: {
    description: 'Weekly starting day index as string (Sunday=0).',
    example: '0'
  },
  interval_only_on_mmdd: {
    description: 'Month/day filters; supports single tokens and ranges.',
    example: '07-04, 11-15..02-10'
  },
  interval_only_on_yyyymmdd: {
    description: 'Absolute date filters; supports single tokens and ranges.',
    example: '2026-12-25, 2026-03-01..2026-03-31'
  },
  interval_only_on_computed_dates: {
    description: 'Computed-date slug filters from the catalog.',
    example: 'mothers-day, thanksgiving'
  }
}

const UPDATE_SECS_OPTIONS = [
  { value: '60', label: '1 Minute' },
  { value: '120', label: '2 Minutes' },
  { value: '180', label: '3 Minutes' },
  { value: '240', label: '4 Minutes' },
  { value: '300', label: '5 Minutes' },
  { value: '600', label: '10 Minutes' },
  { value: '900', label: '15 Minutes' },
  { value: '1200', label: '20 Minutes' },
  { value: '1800', label: '30 Minutes' },
  { value: '3600', label: '1 Hour' },
  { value: '7200', label: '2 Hours' },
  { value: '14400', label: '4 Hours' },
  { value: '86400', label: '1 Day' },
  { value: '604800', label: '1 Week' },
  { value: '31556952', label: '1 Year' }
]

const emptyPolicy = (): CadencePolicy => ({
  version: 1,
  defaults: {},
  enforced: {},
  derived: {},
  requirements: { allowedValues: {}, ignoreFields: {} }
})

const splitTokens = (value: string): string[] =>
  value
    .split(',')
    .map(v => v.trim())
    .filter(v => !!v)

const getFieldKind = (field: string): FieldKind => FIELD_OPTIONS.find(item => item.key === field)?.kind ?? 'scalar'

const getDefaultRuleValue = (field: string): RuleValue => (getFieldKind(field) === 'scalar' ? '' : [])

const getFieldGuidanceText = (field: string): string => {
  const guidance = FIELD_GUIDANCE[field]
  if (!guidance) return ''
  return `${guidance.description} Example: ${guidance.example}`
}

const summarizeLabels = (labels: string[]): string => {
  if (!labels.length) return 'None selected'
  const limit = 3
  const visible = labels.slice(0, limit)
  const remaining = labels.length - limit
  return remaining > 0 ? `${visible.join(', ')}, +${remaining} more` : visible.join(', ')
}

const selectDisplayStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block'
}

const formatUpdateSecsValue = (value: string): string => {
  const option = UPDATE_SECS_OPTIONS.find(item => item.value === value)
  if (!option) return value
  return `${option.label} (${option.value})`
}

const FieldGuidanceRow: React.FC<{
  field: string
  firstColumnWidth: number
  remainingColSpan: number
}> = ({ field, firstColumnWidth, remainingColSpan }) => {
  const guidance = getFieldGuidanceText(field)
  if (!guidance) return null

  return (
    <TableRow>
      <TableCell
        style={{ width: firstColumnWidth, borderTop: 'none', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
      />
      <TableCell
        colSpan={remainingColSpan}
        style={{ borderTop: 'none', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
        <Typography variant='caption' color='textSecondary'>
          {guidance}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

const parsePolicy = (value: string): { policy: CadencePolicy; error: string | null } => {
  try {
    const parsed = JSON.parse(value)
    if (!parsed || typeof parsed !== 'object') {
      return { policy: emptyPolicy(), error: 'Policy JSON must be an object.' }
    }
    const version = parsed.version ?? 1
    if (version !== 1) {
      return {
        policy: emptyPolicy(),
        error: 'Unsupported cadence policy version. Only version 1 is supported.'
      }
    }
    return {
      policy: {
        version: 1,
        defaults: parsed.defaults || {},
        enforced: parsed.enforced || {},
        derived: parsed.derived || {},
        requirements: {
          allowedValues: parsed.requirements?.allowedValues || {},
          ignoreFields: parsed.requirements?.ignoreFields || {}
        },
        notes: parsed.notes
      },
      error: null
    }
  } catch {
    return { policy: emptyPolicy(), error: 'Invalid JSON.' }
  }
}

export const CadencePolicyEditor: React.FC<Props> = ({ value, onChange }) => {
  const [rawMode, setRawMode] = React.useState(false)
  const [rawValue, setRawValue] = React.useState(value || JSON.stringify(emptyPolicy(), null, 2))
  const [rawError, setRawError] = React.useState<string | null>(null)
  const [{ policy }, setState] = React.useState(() => parsePolicy(rawValue))
  const { data: computedCatalogData } = useComputedDateCatalog()

  const computedDateOptions = React.useMemo(() => computedCatalogData?.computedDateCatalog ?? [], [computedCatalogData])

  React.useEffect(() => {
    if (value == null || value === '') {
      setState({ policy: emptyPolicy(), error: null })
      setRawValue('')
      setRawError(null)
      return
    }
    const parsed = parsePolicy(value)
    setState(parsed)
    setRawValue(value)
    setRawError(parsed.error)
  }, [value])

  const sync = (nextPolicy: CadencePolicy) => {
    const nextRaw = JSON.stringify(nextPolicy, null, 2)
    setState({ policy: nextPolicy, error: null })
    setRawValue(nextRaw)
    setRawError(null)
    onChange(nextRaw, null)
  }

  const onRawChange = (next: string) => {
    setRawValue(next)
    const parsed = parsePolicy(next)
    setRawError(parsed.error)
    if (!parsed.error) {
      setState(parsed)
    }
    onChange(next, parsed.error)
  }

  const updateValueRule = (group: 'defaults' | 'enforced', field: string, next: ValueRule) => {
    sync({
      ...policy,
      [group]: {
        ...policy[group],
        [field]: next
      }
    })
  }

  const removeValueRule = (group: 'defaults' | 'enforced', field: string) => {
    const next = { ...policy[group] }
    delete next[field]
    sync({ ...policy, [group]: next })
  }

  const renameValueRuleField = (group: 'defaults' | 'enforced', oldField: string, nextField: string) => {
    const currentRule = policy[group][oldField]
    if (!currentRule) return

    const nextGroup = { ...policy[group] }
    delete nextGroup[oldField]
    nextGroup[nextField] = {
      value: getDefaultRuleValue(nextField),
      note: currentRule.note
    }
    sync({
      ...policy,
      [group]: nextGroup
    })
  }

  const updateDerivedRule = (field: string, note: string) => {
    sync({
      ...policy,
      derived: {
        ...policy.derived,
        [field]: { note }
      }
    })
  }

  const removeDerivedRule = (field: string) => {
    const next = { ...policy.derived }
    delete next[field]
    sync({ ...policy, derived: next })
  }

  const renameDerivedRuleField = (oldField: string, nextField: string) => {
    const currentRule = policy.derived[oldField]
    if (!currentRule) return

    const nextDerived = { ...policy.derived }
    delete nextDerived[oldField]
    nextDerived[nextField] = { note: currentRule.note || '' }
    sync({
      ...policy,
      derived: nextDerived
    })
  }

  const updateAllowedValuesRule = (field: string, rule: AllowedValuesRule) => {
    sync({
      ...policy,
      requirements: {
        ...policy.requirements,
        allowedValues: {
          ...policy.requirements.allowedValues,
          [field]: rule
        }
      }
    })
  }

  const removeAllowedValuesRule = (field: string) => {
    const next = { ...policy.requirements.allowedValues }
    delete next[field]
    sync({
      ...policy,
      requirements: {
        ...policy.requirements,
        allowedValues: next
      }
    })
  }

  const renameAllowedValuesRuleField = (oldField: string, nextField: string) => {
    const currentRule = policy.requirements.allowedValues[oldField]
    if (!currentRule) return

    const nextAllowedValues = { ...policy.requirements.allowedValues }
    delete nextAllowedValues[oldField]
    nextAllowedValues[nextField] = {
      values: [],
      note: currentRule.note
    }
    sync({
      ...policy,
      requirements: {
        ...policy.requirements,
        allowedValues: nextAllowedValues
      }
    })
  }

  const updateIgnoreFieldRule = (field: string, rule: IgnoreFieldRule) => {
    sync({
      ...policy,
      requirements: {
        ...policy.requirements,
        ignoreFields: {
          ...policy.requirements.ignoreFields,
          [field]: rule
        }
      }
    })
  }

  const removeIgnoreFieldRule = (field: string) => {
    const next = { ...policy.requirements.ignoreFields }
    delete next[field]
    sync({
      ...policy,
      requirements: {
        ...policy.requirements,
        ignoreFields: next
      }
    })
  }

  const renameIgnoreFieldRuleField = (oldField: string, nextField: string) => {
    const currentRule = policy.requirements.ignoreFields[oldField]
    if (!currentRule) return

    const nextIgnoreFields = { ...policy.requirements.ignoreFields }
    delete nextIgnoreFields[oldField]
    nextIgnoreFields[nextField] = {
      note: currentRule.note
    }
    sync({
      ...policy,
      requirements: {
        ...policy.requirements,
        ignoreFields: nextIgnoreFields
      }
    })
  }

  const addRule = (group: 'defaults' | 'enforced' | 'derived' | 'allowedValues' | 'ignoreFields') => {
    const usedFields = new Set(
      group === 'defaults'
        ? Object.keys(policy.defaults)
        : group === 'enforced'
        ? Object.keys(policy.enforced)
        : group === 'derived'
        ? Object.keys(policy.derived)
        : group === 'allowedValues'
        ? Object.keys(policy.requirements.allowedValues)
        : Object.keys(policy.requirements.ignoreFields)
    )
    const nextField = FIELD_OPTIONS.find(option => !usedFields.has(option.key))
    if (!nextField) return

    if (group === 'defaults' || group === 'enforced') {
      const defaultValue = nextField.kind === 'scalar' ? '' : []
      updateValueRule(group, nextField.key, { value: defaultValue })
      return
    }
    if (group === 'derived') {
      updateDerivedRule(nextField.key, '')
      return
    }
    if (group === 'allowedValues') {
      updateAllowedValuesRule(nextField.key, { values: [] })
      return
    }
    updateIgnoreFieldRule(nextField.key, {})
  }

  const renderFieldValueInput = (field: string, valueRule: ValueRule, onRuleChange: (rule: ValueRule) => void) => {
    const option = FIELD_OPTIONS.find(item => item.key === field)
    if (!option) return null

    if (option.key === 'interval_update_secs') {
      return (
        <Select
          variant='outlined'
          fullWidth
          displayEmpty
          value={typeof valueRule.value === 'string' ? valueRule.value : ''}
          renderValue={selectedValue =>
            selectedValue === '' ? 'Null (unset)' : formatUpdateSecsValue(selectedValue as string)
          }
          onChange={event => onRuleChange({ ...valueRule, value: event.target.value as string })}>
          <MenuItem value=''>
            <em>Null (unset)</em>
          </MenuItem>
          {UPDATE_SECS_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {`${option.label} (${option.value})`}
            </MenuItem>
          ))}
        </Select>
      )
    }

    if (option.kind === 'computedDateArray') {
      const selected = Array.isArray(valueRule.value) ? valueRule.value : []
      return (
        <Select
          variant='outlined'
          fullWidth
          multiple
          value={selected}
          SelectDisplayProps={{ style: selectDisplayStyle }}
          renderValue={selectedValues => {
            const labels = (selectedValues as string[]).map(value => {
              const entry = computedDateOptions.find(option => option.slug === value)
              return entry ? `${entry.label} (${entry.slug})` : value
            })
            return summarizeLabels(labels)
          }}
          onChange={event => onRuleChange({ ...valueRule, value: event.target.value as string[] })}>
          {computedDateOptions.map(entry => (
            <MenuItem key={entry.slug} value={entry.slug}>
              {`${entry.label} (${entry.slug})`}
            </MenuItem>
          ))}
        </Select>
      )
    }

    if (option.kind === 'array') {
      return (
        <TextField
          variant='outlined'
          fullWidth
          value={Array.isArray(valueRule.value) ? valueRule.value.join(', ') : ''}
          placeholder='Comma separated values (leave blank to treat as null)'
          onChange={event => onRuleChange({ ...valueRule, value: splitTokens(event.target.value) })}
        />
      )
    }

    return (
      <TextField
        variant='outlined'
        fullWidth
        value={typeof valueRule.value === 'string' ? valueRule.value : ''}
        placeholder='Leave blank to treat as null'
        onChange={event => onRuleChange({ ...valueRule, value: event.target.value })}
      />
    )
  }

  const renderAllowedValuesInput = (field: string, rule: AllowedValuesRule) => {
    const option = FIELD_OPTIONS.find(item => item.key === field)
    if (!option) return null

    if (option.key === 'interval_update_secs') {
      return (
        <Select
          variant='outlined'
          fullWidth
          multiple
          value={rule.values}
          SelectDisplayProps={{ style: selectDisplayStyle }}
          renderValue={selectedValues => summarizeLabels((selectedValues as string[]).map(formatUpdateSecsValue))}
          onChange={event => updateAllowedValuesRule(field, { ...rule, values: event.target.value as string[] })}>
          {UPDATE_SECS_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {`${option.label} (${option.value})`}
            </MenuItem>
          ))}
        </Select>
      )
    }

    if (option.kind === 'computedDateArray') {
      return (
        <Select
          variant='outlined'
          fullWidth
          multiple
          value={rule.values}
          SelectDisplayProps={{ style: selectDisplayStyle }}
          renderValue={selectedValues => {
            const labels = (selectedValues as string[]).map(value => {
              const entry = computedDateOptions.find(option => option.slug === value)
              return entry ? `${entry.label} (${entry.slug})` : value
            })
            return summarizeLabels(labels)
          }}
          onChange={event => updateAllowedValuesRule(field, { ...rule, values: event.target.value as string[] })}>
          {computedDateOptions.map(entry => (
            <MenuItem key={entry.slug} value={entry.slug}>
              {`${entry.label} (${entry.slug})`}
            </MenuItem>
          ))}
        </Select>
      )
    }

    return (
      <TextField
        variant='outlined'
        fullWidth
        value={rule.values.join(', ')}
        placeholder='Comma separated values'
        onChange={event => updateAllowedValuesRule(field, { ...rule, values: splitTokens(event.target.value) })}
      />
    )
  }

  const renderValueRuleGroup = (group: 'defaults' | 'enforced') => (
    <>
      <Typography variant='subtitle2'>{group === 'defaults' ? 'Defaults' : 'Enforced'}</Typography>
      <Typography variant='body2' color='textSecondary'>
        {group === 'defaults'
          ? 'Default values are applied at the laravel level when no explicit value is provided - works for any installable.'
          : 'Enforced values are applied at the laravel level and override any passed value - works for any installable.'}
      </Typography>
      <Table size='small' style={{ tableLayout: 'fixed', width: '100%' }}>
        {Object.entries(policy[group]).map(([field, rule]) => (
          <React.Fragment key={`${group}-${field}`}>
            <TableRow>
              <TableCell style={{ width: 220, minWidth: 220, verticalAlign: 'top', borderBottom: 'none' }}>
                <Select
                  variant='outlined'
                  fullWidth
                  value={field}
                  onChange={event => {
                    const nextField = event.target.value as string
                    renameValueRuleField(group, field, nextField)
                  }}>
                  {FIELD_OPTIONS.filter(
                    option => option.key === field || !Object.prototype.hasOwnProperty.call(policy[group], option.key)
                  ).map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell style={{ minWidth: 360, verticalAlign: 'top', borderBottom: 'none' }}>
                <Box display='flex' flexWrap='wrap' gridGap={12}>
                  <Box flex='1 1 360px'>
                    {renderFieldValueInput(field, rule, nextRule => updateValueRule(group, field, nextRule))}
                  </Box>
                  <Box flex='1 1 320px'>
                    <TextField
                      variant='outlined'
                      fullWidth
                      placeholder='Note'
                      value={rule.note || ''}
                      onChange={event => updateValueRule(group, field, { ...rule, note: event.target.value })}
                    />
                  </Box>
                </Box>
              </TableCell>
              <TableCell style={{ width: 90, minWidth: 90, verticalAlign: 'top', borderBottom: 'none' }}>
                <Button onClick={() => removeValueRule(group, field)}>Remove</Button>
              </TableCell>
            </TableRow>
            <FieldGuidanceRow field={field} firstColumnWidth={220} remainingColSpan={2} />
          </React.Fragment>
        ))}
      </Table>
      <Button onClick={() => addRule(group)}>Add {group === 'defaults' ? 'default' : 'enforced'} field</Button>
      <Box height={16} />
    </>
  )

  const renderDerivedGroup = () => (
    <>
      <Typography variant='subtitle2'>Derived</Typography>
      <Typography variant='body2' color='textSecondary'>
        Derived fields are produced by external logic; use notes to describe how they are computed to help AI agents.
      </Typography>
      <Table size='small' style={{ tableLayout: 'fixed', width: '100%' }}>
        {Object.entries(policy.derived).map(([field, rule]) => (
          <React.Fragment key={`derived-${field}`}>
            <TableRow>
              <TableCell style={{ width: 220, minWidth: 220, verticalAlign: 'top', borderBottom: 'none' }}>
                <Select
                  variant='outlined'
                  fullWidth
                  value={field}
                  onChange={event => {
                    const nextField = event.target.value as string
                    renameDerivedRuleField(field, nextField)
                  }}>
                  {FIELD_OPTIONS.filter(
                    option => option.key === field || !Object.prototype.hasOwnProperty.call(policy.derived, option.key)
                  ).map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell style={{ minWidth: 360, verticalAlign: 'top', borderBottom: 'none' }}>
                <TextField
                  variant='outlined'
                  fullWidth
                  placeholder='Note'
                  value={rule.note || ''}
                  onChange={event => updateDerivedRule(field, event.target.value)}
                />
              </TableCell>
              <TableCell style={{ width: 90, minWidth: 90, verticalAlign: 'top', borderBottom: 'none' }}>
                <Button onClick={() => removeDerivedRule(field)}>Remove</Button>
              </TableCell>
            </TableRow>
            <FieldGuidanceRow field={field} firstColumnWidth={220} remainingColSpan={2} />
          </React.Fragment>
        ))}
      </Table>
      <Button onClick={() => addRule('derived')}>Add derived field</Button>
      <Box height={16} />
    </>
  )

  const renderAllowedValuesGroup = () => (
    <>
      <Typography variant='subtitle2'>Requirements: Allowed Values</Typography>
      <Typography variant='body2' color='textSecondary'>
        Allowed values constrain which inputs are valid for each field - intended to help AI agents understand the
        expected inputs for each field. Not currently enforced at the laravel level.
      </Typography>
      <Table size='small' style={{ tableLayout: 'fixed', width: '100%' }}>
        {Object.entries(policy.requirements.allowedValues).map(([field, rule]) => (
          <React.Fragment key={`allowed-${field}`}>
            <TableRow>
              <TableCell style={{ width: 220, minWidth: 220, verticalAlign: 'top', borderBottom: 'none' }}>
                <Select
                  variant='outlined'
                  fullWidth
                  value={field}
                  onChange={event => {
                    const nextField = event.target.value as string
                    renameAllowedValuesRuleField(field, nextField)
                  }}>
                  {FIELD_OPTIONS.filter(
                    option =>
                      option.key === field ||
                      !Object.prototype.hasOwnProperty.call(policy.requirements.allowedValues, option.key)
                  ).map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell style={{ minWidth: 360, verticalAlign: 'top', borderBottom: 'none' }}>
                <Box display='flex' flexWrap='wrap' gridGap={12}>
                  <Box flex='1 1 360px'>{renderAllowedValuesInput(field, rule)}</Box>
                  <Box flex='1 1 320px'>
                    <TextField
                      variant='outlined'
                      fullWidth
                      placeholder='Note'
                      value={rule.note || ''}
                      onChange={event => updateAllowedValuesRule(field, { ...rule, note: event.target.value })}
                    />
                  </Box>
                </Box>
              </TableCell>
              <TableCell style={{ width: 90, minWidth: 90, verticalAlign: 'top', borderBottom: 'none' }}>
                <Button onClick={() => removeAllowedValuesRule(field)}>Remove</Button>
              </TableCell>
            </TableRow>
            <FieldGuidanceRow field={field} firstColumnWidth={220} remainingColSpan={2} />
          </React.Fragment>
        ))}
      </Table>
      <Button onClick={() => addRule('allowedValues')}>Add allowed-values field</Button>
    </>
  )

  const renderIgnoreFieldsGroup = () => (
    <>
      <Typography variant='subtitle2'>Requirements: Ignore Fields</Typography>
      <Typography variant='body2' color='textSecondary'>
        Ignore fields are not used to determine run eligibility, and their user-facing controls should be hidden -
        intended to help AI agents understand which fields are not needed. Not currently enforced at the laravel level.
      </Typography>
      <Table size='small' style={{ tableLayout: 'fixed', width: '100%' }}>
        {Object.entries(policy.requirements.ignoreFields).map(([field, rule]) => (
          <React.Fragment key={`ignore-${field}`}>
            <TableRow>
              <TableCell style={{ width: 220, minWidth: 220, verticalAlign: 'top', borderBottom: 'none' }}>
                <Select
                  variant='outlined'
                  fullWidth
                  value={field}
                  onChange={event => {
                    const nextField = event.target.value as string
                    renameIgnoreFieldRuleField(field, nextField)
                  }}>
                  {FIELD_OPTIONS.filter(
                    option =>
                      option.key === field ||
                      !Object.prototype.hasOwnProperty.call(policy.requirements.ignoreFields, option.key)
                  ).map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell style={{ minWidth: 360, verticalAlign: 'top', borderBottom: 'none' }}>
                <TextField
                  variant='outlined'
                  fullWidth
                  placeholder='Note (optional)'
                  value={rule.note || ''}
                  onChange={event => updateIgnoreFieldRule(field, { note: event.target.value || undefined })}
                />
              </TableCell>
              <TableCell style={{ width: 90, minWidth: 90, verticalAlign: 'top', borderBottom: 'none' }}>
                <Button onClick={() => removeIgnoreFieldRule(field)}>Remove</Button>
              </TableCell>
            </TableRow>
            <FieldGuidanceRow field={field} firstColumnWidth={220} remainingColSpan={2} />
          </React.Fragment>
        ))}
      </Table>
      <Button onClick={() => addRule('ignoreFields')}>Add ignore-field</Button>
    </>
  )

  return (
    <Box>
      {rawMode ? (
        <TextField
          variant='outlined'
          multiline
          minRows={14}
          fullWidth
          value={rawValue}
          onChange={event => onRawChange(event.target.value)}
          error={!!rawError}
          helperText={rawError || 'Edit raw cadence policy JSON.'}
        />
      ) : (
        <>
          <Card style={{ padding: 16, marginBottom: 16 }}>{renderValueRuleGroup('defaults')}</Card>
          <Card style={{ padding: 16, marginBottom: 16 }}>{renderValueRuleGroup('enforced')}</Card>
          <Card style={{ padding: 16, marginBottom: 16 }}>{renderDerivedGroup()}</Card>
          <Card style={{ padding: 16, marginBottom: 16 }}>{renderAllowedValuesGroup()}</Card>
          <Card style={{ padding: 16, marginBottom: 16 }}>{renderIgnoreFieldsGroup()}</Card>
          <Card style={{ padding: 16 }}>
            <Typography variant='subtitle2'>Top-level Notes</Typography>
            <TextField
              variant='outlined'
              fullWidth
              multiline
              minRows={3}
              value={policy.notes || ''}
              onChange={event => sync({ ...policy, notes: event.target.value || undefined })}
            />
          </Card>
        </>
      )}
      <Box mt={2}>
        <FormControlLabel
          control={<Switch checked={rawMode} onChange={event => setRawMode(event.target.checked)} />}
          label='Raw JSON mode'
        />
      </Box>
    </Box>
  )
}
